import React, { useState } from "react";
import { Card, CardHeader, CardContent, Button, Divider, Alert, Table, TableBody, TableCell, TableHead, TableRow, IconButton, TextField, Box, Switch, Typography, LinearProgress, AlertTitle } from "@mui/material";
import { useFileApi } from "../../Hooks/useFileApi";
import { useNotification } from '../NotificationContext';
import BackupIcon from '@mui/icons-material/Backup';
import DeleteIcon from '@mui/icons-material/Delete';
import ShareIcon from '@mui/icons-material/Share';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import axios from 'axios';

const UPLOAD_FOLDER = 'home/prod-unprocessed';

function FileUploadComponent() {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const notify = useNotification();
    const [showTextView, setShowTextView] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [uploadStartTimes, setUploadStartTimes] = useState({});
    const { getUploadUrl } = useFileApi();

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFiles((prev) => [...prev, ...files]);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const files = Array.from(event.dataTransfer.files);
        setSelectedFiles((prev) => [...prev, ...files]);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDeleteFile = (index) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleShare = (fileName) => {
        const url = `${window.location.origin}/download?filename=${UPLOAD_FOLDER}/${encodeURIComponent(fileName)}`;
        navigator.clipboard.writeText(url);
        notify({ message: `Link for ${fileName} copied to clipboard!`, severity: 'success' });
    };

    const handlePreview = (fileName) => {
        window.open(`/preview?filename=${encodeURIComponent(`${UPLOAD_FOLDER}/${fileName}`)}`, '_blank')
    };

    const handleCopyAllLinks = () => {
        const allLinks = uploadedFiles.map(file => 
            `${window.location.origin}/download?filename=${UPLOAD_FOLDER}/${encodeURIComponent(file.name)}`
        ).join('\n');

        navigator.clipboard.writeText(allLinks);
        notify({ message: 'All file links copied to clipboard!', severity: 'success' });
    };

    const handleDownload = () => {
        const fileNames = uploadedFiles.map(file => 
            `${window.location.origin}/download?filename=${UPLOAD_FOLDER}/${encodeURIComponent(file.name)}`
        ).join('\n');
        const blob = new Blob([fileNames], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'uploaded_files.txt';
        a.click();
        URL.revokeObjectURL(url);
        notify({ message: 'Uploaded files list downloaded!', severity: 'success' });
    };

    const formatTimeRemaining = (seconds) => {
        if (!seconds || !isFinite(seconds)) return 'Calculating...';
        
        if (seconds < 60) {
            return `${Math.round(seconds)}s`;
        } else if (seconds < 3600) {
            const minutes = Math.floor(seconds / 60);
            const secs = Math.round(seconds % 60);
            return `${minutes}m ${secs}s`;
        } else {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            return `${hours}h ${minutes}m`;
        }
    };

    const calculateTimeRemaining = (fileName, currentProgress, fileSize) => {
        const startTime = uploadStartTimes[fileName];
        if (!startTime || currentProgress <= 0) return null;
        
        const elapsedTime = (Date.now() - startTime) / 1000;
        const uploadedBytes = (currentProgress / 100) * fileSize;
        const uploadSpeed = uploadedBytes / elapsedTime;
        
        if (uploadSpeed <= 0) return null;
        
        const remainingBytes = fileSize - uploadedBytes;
        const timeRemaining = remainingBytes / uploadSpeed;
        
        return timeRemaining;
    };

    const uploadFileToUrl = async (file, uploadUrl, onProgress) => {
        return axios.put(uploadUrl, file, {
            headers: {
                'Content-Type': file.type || 'application/octet-stream',
                'x-ms-blob-type': 'BlockBlob'
            },
            onUploadProgress: (progressEvent) => {
                if (progressEvent.total && onProgress) {
                    const percentComplete = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    onProgress(percentComplete);
                }
            }
        });
    };

    const handleUpload = async () => {
        setIsUploading(true);
        setUploadProgress({});
        setUploadStartTimes({});

        try {
            const uploadPromises = selectedFiles.map(async (file) => {
                try {
                    const uploadUrl = await getUploadUrl(file.name);
                    
                    setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
                    setUploadStartTimes(prev => ({ ...prev, [file.name]: Date.now() }));
                    
                    await uploadFileToUrl(file, uploadUrl, (progress) => {
                        setUploadProgress(prev => ({ 
                            ...prev, 
                            [file.name]: progress 
                        }));
                    });
                    
                    setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
                    
                    return { success: true, fileName: file.name };
                } catch (error) {
                    console.error('Upload error for file:', file.name, error);
                    setUploadProgress(prev => ({ ...prev, [file.name]: -1 }));
                    return { success: false, fileName: file.name, error: error.message };
                }
            });
            
            const results = await Promise.all(uploadPromises);
            const failures = results.filter(r => !r.success);
            const successes = results.filter(r => r.success);

            if (successes.length > 0) {
                setUploadedFiles((prev) => [...prev, ...successes.map(r => ({ name: r.fileName }))]);
            }

            if (failures.length === 0) {
                setSelectedFiles([]);
                notify({ message: 'All files uploaded successfully!', severity: 'success' });
            } else {
                const failedFiles = failures.map(f => f.fileName).join(', ');
                notify({ message: `Upload completed with errors. Failed files: ${failedFiles}`, severity: 'warning' });
                const successfullyUploaded = results.filter(r => r.success).map(r => r.fileName);
                setSelectedFiles(prev => prev.filter(file => !successfullyUploaded.includes(file.name)));
            }
            
            setTimeout(() => {
                setUploadProgress({});
                setUploadStartTimes({});
            }, 3000);
            
        } catch (error) {
            console.error('Upload error:', error);
            notify({ message: `Upload failed: ${error.message || 'Please try again.'}`, severity: 'error' });
        } finally {
            setIsUploading(false);
        }
    };

    const toggleView = () => {
        setShowTextView(!showTextView);
    };

    const getUploadedFilesText = () => {
        return uploadedFiles
            .map(file => `${window.location.origin}/download?filename=${UPLOAD_FOLDER}/${encodeURIComponent(file.name)}`)
            .join('\n');
    };

    const getProgressColor = (progress) => {
        if (progress === -1) return 'error';
        if (progress === 100) return 'success';
        return 'primary';
    };

    const getProgressText = (fileName, progress, fileSize) => {
        if (progress === -1) return 'Failed';
        if (progress === 100) return 'Complete';
        if (progress === 0) return 'Starting...';
        
        const timeRemaining = calculateTimeRemaining(fileName, progress, fileSize);
        const timeText = timeRemaining ? ` - ${formatTimeRemaining(timeRemaining)} remaining` : '';
        
        return `${progress}%${timeText}`;
    };

    return (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "94vh" }}>
            <Card sx={{ minWidth: "100%", height: "100%", textAlign: 'left', overflow: "auto", border: '1px solid', borderColor: 'divider' }}>
                <CardHeader title="Upload Files" sx={{ fontWeight: 500, fontSize: "1.25rem" }}/>
                <Divider />
                <CardContent sx={{ ml: 5, mr: 5, mt: 2, minHeight: "80%" }}>
                    <Alert 
                        severity="info" 
                        icon={<InfoIcon />}
                        sx={{ marginBottom: 2, borderRadius: 1 }}
                    >
                        <AlertTitle>Browser Compatibility</AlertTitle>
                        If you experience issues with uploads, make sure you are connected to GM networking or VPN, or try using Microsoft Edge browser.
                    </Alert>
                    <Typography variant="body2" sx={{ marginBottom: 2 }}>
                        Select files to upload using drag-and-drop or the button below.
                    </Typography>
                    <Box
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        sx={{
                            border: "1px dashed",
                            borderColor: "divider",
                            borderRadius: "4px",
                            padding: "20px",
                            textAlign: "center",
                            marginBottom: "16px",
                            backgroundColor: "action.hover"
                        }}
                    >
                        <BackupIcon sx={{ fontSize: 40, color: "text.secondary", marginBottom: 1 }} /><br />
                        <Typography variant="body2" color="text.secondary">
                            Drag and drop files here
                        </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                        <Button
                            variant="outlined"
                            component="label"
                            sx={{ 
                                borderColor: 'divider',
                                textTransform: 'none'
                            }}
                        >
                            Select Files
                            <input
                                type="file"
                                multiple
                                hidden
                                onChange={handleFileChange}
                            />
                        </Button>
                    </Box>

                    {selectedFiles.length > 0 && (
                        <Box sx={{ maxHeight: '400px', overflowY: 'auto', mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                        <TableCell sx={{ fontWeight: 500 }}>Number</TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>File Name</TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>File Size (KiB)</TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>Progress</TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {selectedFiles.map((file, index) => {
                                        const progress = uploadProgress[file.name];
                                        const hasProgress = progress !== undefined;
                                        
                                        return (
                                            <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                                                <TableCell>{index+1}</TableCell>
                                                <TableCell>{file.name}</TableCell>
                                                <TableCell>{(file.size / 1024).toFixed(2)}</TableCell>
                                                <TableCell sx={{ minWidth: 200 }}>
                                                    {hasProgress ? (
                                                        <Box>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={progress === -1 ? 100 : progress}
                                                                color={getProgressColor(progress)}
                                                                sx={{ mb: 1 }}
                                                            />
                                                            <Typography variant="caption" color="textSecondary">
                                                                {getProgressText(file.name, progress, file.size)}
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="caption" color="textSecondary">
                                                            Ready to upload
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        onClick={() => handleDeleteFile(index)}
                                                        aria-label="delete file"
                                                        disabled={isUploading}
                                                        sx={{ 
                                                            borderColor: 'divider',
                                                            '&:hover': { backgroundColor: 'action.selected' }
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Box>
                    )}

                    {uploadedFiles.length > 0 && (
                        <>
                            <Box sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Typography variant="body2">Uploaded Files View:</Typography>
                                <Switch
                                    checked={showTextView}
                                    onChange={toggleView}
                                    label="Toggle View"
                                />
                                <Typography variant="body2">{showTextView ? 'Text View' : 'List View'}</Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<FileCopyIcon />}
                                    onClick={handleCopyAllLinks}
                                    disabled={uploadedFiles.length > 100}
                                    sx={{ 
                                        borderColor: 'divider',
                                        textTransform: 'none'
                                    }}
                                >
                                    Copy All Links
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownload}
                                    sx={{ 
                                        borderColor: 'divider',
                                        textTransform: 'none'
                                    }}
                                >
                                    Download List
                                </Button>
                            </Box>
                            
                            <Box sx={{ maxHeight: '400px', overflowY: 'auto', mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                {showTextView ? (
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={5}
                                        value={getUploadedFilesText()}
                                        slotProps={{
                                            input: {
                                                readOnly: true,
                                            },
                                        }}
                                        variant="outlined"
                                        label="Uploaded Files"
                                        sx={{ 
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: 'background.paper'
                                            }
                                        }}
                                    />
                                ) : (
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                                <TableCell sx={{ fontWeight: 500 }}>Number</TableCell>
                                                <TableCell sx={{ fontWeight: 500 }}>File Name</TableCell>
                                                <TableCell sx={{ fontWeight: 500 }}>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {uploadedFiles.map((file, index) => (
                                                <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                                                    <TableCell>{index+1}</TableCell>
                                                    <TableCell>{file.name}</TableCell>
                                                    <TableCell>
                                                        <IconButton
                                                            onClick={() => handleShare(file.name)}
                                                            aria-label="share file"
                                                            sx={{ 
                                                                borderColor: 'divider',
                                                                marginRight: 1,
                                                                '&:hover': { backgroundColor: 'action.selected' }
                                                            }}
                                                        >
                                                            <ShareIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            onClick={() => handlePreview(file.name)}
                                                            aria-label="preview file"
                                                            sx={{ 
                                                                borderColor: 'divider',
                                                                '&:hover': { backgroundColor: 'action.selected' }
                                                            }}
                                                        >
                                                            <FileOpenIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </Box>
                        </>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                        <Button
                            onClick={handleUpload}
                            variant="outlined"
                            disabled={selectedFiles.length === 0 || isUploading}
                            sx={{ 
                                borderColor: 'divider',
                                textTransform: 'none'
                            }}
                        >
                            {isUploading ? 'Uploading...' : 'Upload'}
                        </Button>
                    </Box>

                </CardContent>
            </Card>
        </div>
    );
}

export default FileUploadComponent;
